const DB_NAME = 'rid-pdf-cache';
const STORE_NAME = 'pdfs';
const DB_VERSION = 1;

type StoredPdf = Blob | { bytes: ArrayBuffer; type: string };

export function createPdfCacheId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function writePdf(key: string, value: StoredPdf): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let request: IDBRequest;
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const fail = () => {
        db.close();
        reject(request?.error ?? tx.error ?? new Error('PDF storage transaction was aborted.'));
      };
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = fail;
      tx.onabort = fail;
      request = tx.objectStore(STORE_NAME).put(value, key);
    } catch (error) {
      db.close();
      reject(error);
    }
  });
}

export async function storePdf(key: string, blob: Blob): Promise<void> {
  try {
    // Keep the efficient native Blob path where supported.
    await writePdf(key, blob);
  } catch (error) {
    // WebKit can reject Blob structured cloning (including private contexts).
    // Retry only this compatibility failure; quota/permission failures remain errors.
    const name = error && typeof error === 'object' && 'name' in error ? error.name : '';
    if (name !== 'UnknownError' && name !== 'DataCloneError') throw error;
    await writePdf(key, { bytes: await blob.arrayBuffer(), type: blob.type });
  }
}

export async function getPdf(key: string): Promise<Blob | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => {
        db.close();
        const record = request.result as StoredPdf | null | undefined;
        if (record == null) resolve(null);
        else if (record instanceof Blob) resolve(record); // Preserve existing cache records.
        else if (record.bytes instanceof ArrayBuffer && typeof record.type === 'string') {
          resolve(new Blob([record.bytes], { type: record.type }));
        } else reject(new Error('Stored PDF has an unsupported format.'));
      };
      const fail = () => { db.close(); reject(request.error ?? tx.error ?? new Error('PDF read was aborted.')); };
      request.onerror = fail;
      tx.onabort = fail;
    } catch (error) {
      db.close();
      reject(error);
    }
  });
}

export async function clearPdfs(keys: string[]): Promise<void> {
  if (keys.length === 0) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => { db.close(); resolve(); };
      const fail = () => { db.close(); reject(tx.error ?? new Error('PDF deletion was aborted.')); };
      tx.onerror = fail;
      tx.onabort = fail;
      const store = tx.objectStore(STORE_NAME);
      for (const key of keys) store.delete(key);
    } catch (error) {
      db.close();
      reject(error);
    }
  });
}
